<template>
  <div class="min-h-screen bg-bochas-bg">
    <ImageBackgroundSection
      seed="federacion-hero"
      :image-url="heroImageUrl"
      :loading-image="heroImageLoading"
      alt="La Federación de Bochas de Córdoba"
      :title="heroMedia?.title || 'La Federación'"
      :subtitle="
        heroMedia?.subtitle ||
        'Organizando y fortaleciendo la comunidad de bochas en Córdoba desde 1985'
      "
    />

    <RevealOnScroll
      v-if="sectionVisible('federacion.historia')"
      as="section"
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:py-24 sm:py-12"
    >
      <div class="flex items-center gap-3 mb-6">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="pasado" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Historias</span>
        </div>
      </div>

      <article
        class="federation-story grid grid-cols-1 md:grid-cols-2 gap-0 items-stretch"
      >
        <img
          v-if="historyImageUrl"
          :src="historyImageUrl"
          :alt="historyMedia?.alt || 'Historia de la Federación'"
          class="w-full h-72 md:h-full object-cover rounded-t-3xl md:rounded-t-none md:rounded-l-3xl"
        />
        <div
          v-else
          class="w-full h-72 md:h-full min-h-72 bg-white/5 rounded-t-3xl md:rounded-t-none md:rounded-l-3xl"
          aria-hidden="true"
        ></div>
        <div
          class="py-10 px-8 bg-[#1A1F2E] border-r-[9px] border-r-solid border-r-[#A52121] rounded-b-3xl md:rounded-b-none md:rounded-r-3xl"
        >
          <h2 class="text-2xl sm:text-3xl font-semibold text-white mb-4">
            Nuestra Historia
          </h2>
          <p class="text-white leading-relaxed font-medium text-sm sm:text-2xl">
            La Federación de Bochas de la Provincia de Córdoba fue fundada el 13
            de mayo de 1967, con el objetivo de unificar y organizar la
            actividad bochófila en toda la provincia. Impulsada por
            representantes de distintas asociaciones, nació como el organismo
            rector del deporte en Córdoba, siendo su primer presidente Rodolfo
            Angeletti, quien lideró la institución durante más de 25 años.
          </p>
          <p class="text-white leading-relaxed font-medium text-sm sm:text-2xl">
            En la actualidad, la Federación reúne a 28 asociaciones afiliadas y
            224 clubes, fortaleciendo la práctica de las bochas en toda la
            provincia y formando deportistas que representan a Córdoba con
            destacados resultados a nivel nacional e internacional.
          </p>
        </div>
      </article>
    </RevealOnScroll>

    <RevealOnScroll
      v-if="sectionVisible('federacion.misionVision')"
      as="section"
      :delay="80"
      class="max-w-7xl mx-auto pt-20 px-6 sm:px-6 lg:px-0 lg:pt-0 lg:pb-24 sm:py-12"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-20 sm:gap-6">
        <div
          class="federation-banner p-6 sm:p-10 rounded-lg bg-degradado-h-bochas flex flex-col gap-2"
        >
          <div class="flex items-start justify-between gap-4">
            <h3 class="text-xl sm:text-3xl font-semibold text-white">
              Nuestra Visión
            </h3>
            <IconFbochas
              name="quote"
              :size="40"
              class="text-white/30 flex-shrink-0"
            />
          </div>
          <p class="text-white leading-relaxed font-medium text-sm sm:text-2xl">
            Ser una federación referente a nivel nacional por su excelencia
            deportiva e institucional, impulsando el crecimiento sostenido de
            las bochas, la participación de nuevas generaciones y consolidando a
            Córdoba como la verdadera Cuna de Campeones.
          </p>
        </div>

        <div
          class="federation-banner p-6 sm:p-10 rounded-lg bg-degradado-h-bochas flex flex-col gap-2"
        >
          <div class="flex items-start justify-between gap-4">
            <h3 class="text-xl sm:text-3xl font-semibold text-white">
              Nuestra Misión
            </h3>
            <IconFbochas
              name="quote"
              :size="40"
              class="text-white/30 flex-shrink-0"
            />
          </div>
          <p class="text-white leading-relaxed font-medium text-sm sm:text-2xl">
            Promover, organizar y desarrollar el deporte de las bochas en toda
            la provincia de Córdoba, acompañando a las asociaciones y clubes
            afiliados, fomentando la formación deportiva, los valores de la
            amistad, el respeto y el trabajo en equipo, y fortaleciendo el
            crecimiento institucional de la disciplina.
          </p>
        </div>
      </div>
    </RevealOnScroll>

    <RevealOnScroll
      v-if="sectionVisible('federacion.autoridades') && authoritySections.length"
      as="section"
      :delay="120"
      class="max-w-7xl mx-auto pt-20 px-6 sm:px-6 lg:px-0 lg:pt-0 lg:pb-24 sm:py-12"
    >
      <div class="flex items-center gap-3">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="users" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Autoridades</span>
        </div>
      </div>
      <div class="space-y-14">
        <div v-for="section in authoritySections" :key="section.title">
          <h2 class="text-2xl sm:text-3xl font-extrabold text-white mb-8">
            {{ section.title }}
          </h2>

          <div
            class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-x-6 gap-y-12"
          >
            <article
              v-for="dir in section.items"
              :key="`${section.title}-${dir.cargo}-${dir.nombre}`"
              class="federation-card card-fbochas overflow-hidden"
            >
              <img
                v-if="dir.photoUrl"
                :src="dir.photoUrl"
                :alt="dir.nombre"
                class="w-full aspect-square object-cover rounded-t-2xl"
              />
              <div
                v-else
                class="flex aspect-square w-full items-center justify-center rounded-t-2xl bg-degradado-bochas text-5xl font-bold text-white"
              >
                {{ initials(dir.nombre) }}
              </div>
              <div
                class="p-3 sm:p-4 border-x border-b rounded-b-2xl border-white"
              >
                <p
                  class="text-sm font-normal uppercase tracking-wide text-white mb-1"
                >
                  {{ dir.cargo }}
                </p>
                <p class="text-white font-semibold text-lg">
                  {{ dir.nombre }}
                </p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </RevealOnScroll>

    <RevealOnScroll
      v-if="sectionVisible('federacion.reglamentacion') && reglamentos.length"
      as="section"
      id="reglamentacion"
      :delay="160"
      class="scroll-mt-24 max-w-7xl mx-auto py-20 px-6 sm:px-6 sm:py-12 lg:px-0 lg:pt-0 lg:pb-24"
    >
      <div class="flex items-center gap-3">
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="newspaper" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Reglamentación</span>
        </div>
      </div>
      <h2 class="text-2xl sm:text-4xl font-semibold text-white mb-8">
        Estatutos y Reglamentos
      </h2>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <article
          v-for="reg in reglamentos"
          :key="reg.titulo"
          class="federation-document card-fbochas p-4 sm:p-6 flex flex-col"
        >
          <div class="flex items-start justify-between gap-2 mb-3">
            <h3 class="text-base sm:text-2xl font-medium text-white">
              {{ reg.titulo }}
            </h3>
            <span class="federation-document__arrow text-white text-4xl"
              >→</span
            >
          </div>
          <p class="text-white text-lg font-normal flex-1">
            {{ reg.descripcion }}
          </p>
          <a
            v-if="reg.url"
            :href="reg.url"
            target="_blank"
            rel="noopener"
            class="mt-4 text-sm font-bold text-bochas-rojo"
          >
            Ver documento
          </a>
        </article>
      </div>
    </RevealOnScroll>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { useSiteMediaImageState } from "@/composables/useSiteMediaImage";
import { obtenerFederationContent } from "@/services/federation/federationContentService";
import {
  crearSiteVisibility,
  obtenerSiteVisibility,
} from "@/services/siteSettings/siteVisibilityService";

const siteVisibility = ref(crearSiteVisibility());
const sectionVisible = (key) => siteVisibility.value[key] !== false;

const {
  imageUrl: heroImageUrl,
  item: heroMedia,
  loading: heroImageLoading,
} = useSiteMediaImageState("federacion.hero");

const {
  imageUrl: historyImageUrl,
  item: historyMedia,
} = useSiteMediaImageState("federacion.historia");

const directiva = ref([]);

const authorityGroupOrder = [
  "Comisión Directiva",
  "Tribunal de Disciplina",
  "Secretario Administrativo",
  "Secretario de Prensa",
];

const authoritySections = computed(() =>
  authorityGroupOrder
    .map((title) => ({
      title,
      items: directiva.value.filter(
        (item) => (item.grupo || "Comisión Directiva") === title,
      ),
    }))
    .filter((section) => section.items.length),
);

const reglamentos = ref([]);

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

onMounted(async () => {
  try {
    const [data, visibility] = await Promise.all([
      obtenerFederationContent(),
      obtenerSiteVisibility(),
    ]);
    siteVisibility.value = visibility;
    if (data.authorities.length) {
      directiva.value = data.authorities.map((item) => ({
        ...item,
        grupo: item.grupo || "Comisión Directiva",
      }));
    }
    if (data.documents.length) reglamentos.value = data.documents;
  } catch (_) {}
});
</script>

<style scoped>
.federation-banner,
.federation-card,
.federation-document {
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 320ms ease, border-color 320ms ease, filter 320ms ease;
}

.federation-card :deep(img) {
  transition: transform 650ms cubic-bezier(0.22, 1, 0.36, 1), filter 450ms ease;
}

.federation-banner:hover,
.federation-card:hover,
.federation-document:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 44px rgba(0, 0, 0, 0.25);
}

.federation-card:hover {
  border-color: rgba(165, 33, 33, 0.5);
}

.federation-banner:hover {
  filter: brightness(1.06);
}

.federation-card:hover :deep(img) {
  transform: scale(1.035);
  filter: brightness(1.06);
}

.federation-document__arrow {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1), color 260ms ease;
}

.federation-document:hover .federation-document__arrow {
  color: #a52121;
  transform: translateX(5px);
}

@media (prefers-reduced-motion: reduce) {
  .federation-banner,
  .federation-card,
  .federation-document,
  .federation-card :deep(img),
  .federation-document__arrow {
    transition: none;
  }

  .federation-banner:hover,
  .federation-card:hover,
  .federation-document:hover,
  .federation-card:hover :deep(img),
  .federation-document:hover .federation-document__arrow {
    transform: none;
  }
}
</style>
